import { invalidate } from "@react-three/fiber";
import { getAddShapeBlockedReason } from "../../../store/addShapeBlockedReason";
import { editorStore } from "../../../store/editorStore";
import { floorplannerStore } from "../../../store/floorplannerStore";
import {
  getUserModel3DTitle,
  UserModel3DLibraryItem,
} from "./userModel3DTypes";
import { commerceStampFromLibraryItem } from "./userModel3DCommerceStamp";
import { getUserModel3DPlanSvgPath } from "./userModel3DPlanSvg";

export const getUserModel3DInsertDimensions = (
  model: UserModel3DLibraryItem,
) => {
  const width = model.dimensions?.width ?? model.bounds?.size?.width ?? 1;
  const depth = model.dimensions?.depth ?? model.bounds?.size?.depth ?? 0.8;
  const height = model.dimensions?.height ?? model.bounds?.size?.height ?? 0.8;
  const maxPlanDimension = Math.max(width, depth, 0.001);
  if (maxPlanDimension <= 2) return { width, depth, height };
  const scale = 2 / maxPlanDimension;
  return {
    width: width * scale,
    depth: depth * scale,
    height: Math.min(Math.max(height * scale, 0.2), 3),
  };
};

export const insertUserModel3D = (
  model: UserModel3DLibraryItem,
): { ok: true; id: string } | { ok: false; reason: string } => {
  if (model.status !== "ready" || !model.optimizedUrl) {
    return { ok: false, reason: "This 3D object is not ready to place." };
  }
  if (floorplannerStore.lockedByOther) {
    return { ok: false, reason: getAddShapeBlockedReason(floorplannerStore) };
  }
  const title = getUserModel3DTitle(model);
  const dimensions = getUserModel3DInsertDimensions(model);
  const [centerX, centerY] = editorStore.getVisibleCenter();
  const svgPath = getUserModel3DPlanSvgPath(model);
  const id = floorplannerStore.addSymbol("svg", [centerX, centerY], {
    name: title,
    svgPath,
    svgLength: dimensions.width,
    svgHeight: dimensions.depth,
    lockAspectRatio: true,
    libraryKey: "USER_MODEL_3D",
    lineColor: 0x000000,
    threeD: {
      sourceMode: "custom",
      modelKey: "living:coffee-table-oval",
      dimensions,
      height: dimensions.height,
      placementMode: "floor",
      collision: "box",
      userModel: {
        modelId: model.id,
        url: model.optimizedUrl,
        version: model.pipelineVersion ?? undefined,
        status: "ready",
        name: title,
        thumbnailUrl: model.thumbnailUrl ?? undefined,
        sourceImageUrl: model.sourceImageUrl ?? undefined,
        dimensions,
        bounds: model.bounds ?? undefined,
        sha256: model.sha256 ?? undefined,
        footprintSvgPath: svgPath,
        ...commerceStampFromLibraryItem(model),
      },
    },
  });
  if (!id) {
    return { ok: false, reason: getAddShapeBlockedReason(floorplannerStore) };
  }
  // addSymbol already records and commits the undo entry for the insert.
  const symbol = floorplannerStore.symbolsMap.get(id);
  if (symbol) {
    editorStore.clearSelections();
    floorplannerStore.updateSymbolProperty(id, "selected", true);
    editorStore.addSelection(symbol);
  }
  invalidate();
  return { ok: true, id };
};
